// Stale-chunk self-heal.
//
// The site is a hash-chunked SPA that auto-deploys on every merge to main. A
// tab left open across a deploy still holds the OLD index.html and main
// bundle, so the next lazy navigation asks for a chunk hash that no longer
// exists on Cloudflare Pages: 404 -> the dynamic import rejects -> the error
// boundary paints a dead end that only a hard refresh clears.
//
// The recovery is a single reload, which fetches the new index.html and the
// chunk hashes that go with it. Everything here exists to make that reload
// happen exactly once.

const RELOAD_KEY = "cch-chunk-reload-at";
const COUNT_KEY = "cch-chunk-reload-count";

// How long the page has to survive after a self-heal reload before we're
// willing to arm the reload again. See markChunkLoadSuccess.
const SETTLE_MS = 5_000;

// Hard ceiling on self-heal reloads for the life of the tab. Nothing clears
// this — see reloadOnceForChunkFailure.
const MAX_RELOADS_PER_SESSION = 2;

function readReloadedAt(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    // Safari private mode and friends can throw on sessionStorage access.
    // Treat it as "already reloaded" — refusing to reload is the safe side.
    return Date.now();
  }
}

function readReloadCount(): number {
  try {
    return Number(sessionStorage.getItem(COUNT_KEY)) || 0;
  } catch {
    // Same trade as readReloadedAt: if we can't read the budget, assume it's
    // spent rather than reload blind.
    return MAX_RELOADS_PER_SESSION;
  }
}

/**
 * True for the "my chunk 404'd" family of failures, across browsers.
 *
 * The message differs by engine — Chrome says "Failed to fetch dynamically
 * imported module", Safari says "error loading dynamically imported module",
 * Firefox says "error loading dynamically imported module" too, and bundlers
 * that predate native ESM raise a named ChunkLoadError. Match all of them.
 */
export function isChunkLoadError(err: unknown): boolean {
  const name = err instanceof Error ? err.name : "";
  if (name === "ChunkLoadError") return true;

  const message = err instanceof Error ? err.message : String(err);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Unable to preload CSS/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Loading (CSS )?chunk .* failed/i.test(message)
  );
}

/**
 * Reload the page once per session to pick up fresh chunk hashes.
 *
 * THE LOOP GUARD IS THE POINT — do not simplify it away. If a deploy is
 * genuinely broken (a chunk that 404s no matter which index.html you hold),
 * an unguarded reload-on-chunk-error turns a white screen into an infinite
 * refresh loop, which is far worse: the customer can't even read the error,
 * and every loop re-hits the origin.
 *
 * There are TWO guards and they do different jobs:
 *
 *   1. RELOAD_KEY — the armed/spent flag. markChunkLoadSuccess re-arms it so
 *      a long session still gets a self-heal for a *second* deploy.
 *   2. COUNT_KEY — a hard ceiling for the life of the tab, which nothing
 *      clears. This one exists because guard 1 alone is a timing argument,
 *      not a bound: on a slow connection the sibling chunks that resolve
 *      after a reload routinely land past SETTLE_MS, which re-arms guard 1
 *      and lets a permanently-broken chunk reload the tab every 6-10s
 *      indefinitely. The ceiling makes that unreachable by construction.
 *
 * Keep both. Deleting the ceiling reopens the slow-network loop; deleting the
 * flag makes the ceiling the only budget and costs long sessions their
 * recovery.
 *
 * @returns true if a reload was triggered (caller should stop rendering).
 */
export function reloadOnceForChunkFailure(): boolean {
  if (typeof window === "undefined") return false;
  if (readReloadCount() >= MAX_RELOADS_PER_SESSION) return false;
  if (readReloadedAt() !== 0) return false;

  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    sessionStorage.setItem(COUNT_KEY, String(readReloadCount() + 1));
  } catch {
    // If we can't record the attempt we can't bound the loop, so don't start one.
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Re-arm the guard after a chunk has loaded successfully.
 *
 * Without this the guard burns itself permanently: one self-heal early in a
 * long session would leave the flag set, and a later deploy in the same
 * session would get no recovery at all.
 *
 * The SETTLE_MS gate is what keeps re-arming from re-opening the loop. Right
 * after a reload, some chunks always resolve fine (the Toaster, the current
 * route) even when the one chunk the user actually wants is permanently
 * broken — clearing on those would let the broken chunk reload us forever.
 * Only a session that stayed healthy past SETTLE_MS counts as recovered.
 *
 * SETTLE_MS is a heuristic, not a bound — on a slow connection a sibling
 * chunk can easily resolve past it. Deliberately, this never touches
 * COUNT_KEY: the hard ceiling is what actually bounds the loop, and re-arming
 * must not be able to lift it.
 */
export function markChunkLoadSuccess(): void {
  if (typeof window === "undefined") return;
  const reloadedAt = readReloadedAt();
  if (reloadedAt === 0) return;
  if (Date.now() - reloadedAt < SETTLE_MS) return;
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // Nothing useful to do; the guard just stays armed.
  }
}
